import type { ReactElement } from 'react'
import { SkeletonRouteCard } from './SkeletonRouteCard'
import styles from './RouteLoading.module.css'

/**
 * Props for {@link RouteLoading}.
 */
export interface RouteLoadingProps {
  /** How many skeleton cards to render. Default 3 (Figma spec §2.6). */
  readonly count?: number
}

const DEFAULT_COUNT = 3

/**
 * Loading state for the route-built panel (Figma spec §2.6). Two rows:
 *
 * 1. `Loading scroll` — `count` × {@link SkeletonRouteCard} (w 140, gap 4),
 *    16px outer padding, mirroring the Results scroll geometry.
 * 2. `Route details row (skeleton)` — a single `338 × 64 r32 bg/secondary`
 *    button placeholder (no label) standing in for the Route Details
 *    button while loading.
 *
 * Shimmer lives in SkeletonRouteCard; under `prefers-reduced-motion:
 * reduce` (Playwright default) the cards render static.
 *
 * `count={0}` still renders the (empty) scroll + button skeleton so a
 * caller can flip loading → results without remounting the panel tree.
 */
export function RouteLoading({ count = DEFAULT_COUNT }: RouteLoadingProps): ReactElement {
  const safeCount = Math.max(0, Math.floor(count))
  return (
    <div className={styles.root} data-testid="route-loading" aria-busy="true" aria-live="polite">
      <ul className={styles.list}>
        {Array.from({ length: safeCount }, (_, idx) => (
          <li key={idx} className={styles.item} data-testid="route-loading-skeleton">
            <SkeletonRouteCard />
          </li>
        ))}
      </ul>
      <div className={styles.detailsRow}>
        <span className={styles.buttonSkeleton} data-testid="route-loading-button-skeleton" />
      </div>
    </div>
  )
}
